import Sidebar from '@rio-cloud/rio-uikit/Sidebar';
import { FormattedMessage } from 'react-intl';
import { useAppDispatch, useAppSelector } from '../../../../configuration/setup/hooks';
import {
    usePostAcknowledgementOfExternalTransportOrderChangesMutation,
    usePostArchiveTransportAssignmentMutation,
} from '../../api/transportOrderApi';
import {
    TransportAssignmentStatus,
    TransportAssignmentType,
    TransportOrder,
    TransportOrderBundle,
} from '../transportAssignment.types';
import { TransportAssignmentSidebarBodyContent } from './TransportAssignmentSidebarBodyContent';
import { getSelectedTransportAssignment, transportAssignmentsSlice } from '../../reducers/transportAssignmentsSlice';
import { FunctionComponent, useEffect } from 'react';
import { handleQueryError } from '../../notifications/ErrorNotification';
import { showSuccessNotification } from '../../notifications/SuccessNotification';
import { SidebarExpander } from './SidebarExpander';
import { getOrderIds } from '../TransportAssignmentUtils';
import classNames from 'classnames';
import { SidebarHeader } from './SidebarHeader';
import { runInBackground, runInBackgroundCallback } from '../../../../configuration/setup/backgroundActions';
import { TransportAssignmentConfirmationForm } from './TransportAssignmentConfirmationForm';

export const TransportAssignmentSidebar: FunctionComponent = () => {
    const selected = useAppSelector(getSelectedTransportAssignment);
    const transportAssignment =
        selected?.type === TransportAssignmentType.TRANSPORT_ORDER ||
        selected?.type === TransportAssignmentType.TRANSPORT_ORDER_BUNDLE
            ? selected
            : undefined;
    return (
        <TransportAssignmentSidebarInternal transportAssignment={transportAssignment} key={transportAssignment?.id} />
    );
};

// eslint-disable-next-line max-lines-per-function
const TransportAssignmentSidebarInternal = ({
    transportAssignment,
}: {
    transportAssignment: TransportOrder | TransportOrderBundle | undefined;
}) => {
    const [acknowledgeChanges] = usePostAcknowledgementOfExternalTransportOrderChangesMutation();
    const [archiveTransportAssignment, archiveRequest] = usePostArchiveTransportAssignmentMutation();

    const dispatch = useAppDispatch();

    useEffect(() => {
        return () => {
            if (transportAssignment !== undefined && transportAssignment.hasUnacknowledgedExternalChanges) {
                runInBackground(
                    acknowledgeChanges({
                        transportOrderIds: getOrderIds(transportAssignment),
                    }),
                );
            }
        };
    }, [acknowledgeChanges, transportAssignment]);

    const sidebarProperties = {
        showHeaderBorder: true,
        resizable: true,
        position: Sidebar.RIGHT,
        minWidth: 400,
        maxWidth: 550,
        width: 550,
        fly: true,
    };
    const isLoading = archiveRequest.isLoading;
    if (transportAssignment === undefined) {
        return <Sidebar {...sidebarProperties} closed />;
    }

    const hasArchiveButton =
        !transportAssignment.isArchived &&
        (transportAssignment.status === TransportAssignmentStatus.CANCELLATION || transportAssignment.isFinalized);

    const closeSidebar = () => {
        dispatch(transportAssignmentsSlice.actions.deselectTransportAssignment());
    };

    const handleArchiveTransportAssignment = runInBackgroundCallback(async () => {
        await archiveTransportAssignment({
            transportOrderIds: getOrderIds(transportAssignment),
            isArchived: true,
        })
            .unwrap()
            .then(() => {
                showSuccessNotification('outboundPortal.notification.archived.success');
                closeSidebar();
            })
            .catch(handleQueryError);
    });

    const title =
        transportAssignment.type === TransportAssignmentType.TRANSPORT_ORDER ? (
            <FormattedMessage id="outboundPortal.transportAssignments.sidebar.header.titleTransportOrder" />
        ) : transportAssignment.type === TransportAssignmentType.TRANSPORT_ORDER_BUNDLE ? (
            <FormattedMessage
                id="outboundPortal.transportAssignments.sidebar.header.titleLoadOrder"
                values={{
                    amount: transportAssignment.numberOfVehicles,
                }}
            />
        ) : (
            <FormattedMessage id="Replace.Me" />
        );

    const footerButton = (
        <div className="display-flex gap-10 flex-row-reverse">
            <button className="btn btn-default" type="button" onClick={closeSidebar}>
                <FormattedMessage id="outboundPortal.close" />
            </button>
            {hasArchiveButton && (
                <button
                    className={classNames('btn btn-danger btn-outline', isLoading ? 'btn-loading' : '')}
                    type="button"
                    disabled={isLoading}
                    onClick={handleArchiveTransportAssignment}
                >
                    <span className="rioglyph rioglyph-folder-closed" />
                    <FormattedMessage id="outboundPortal.transportAssignments.button.archive" />
                </button>
            )}
        </div>
    );

    return (
        <Sidebar
            {...sidebarProperties}
            title={title}
            titleClassName="padding-left-10"
            closed={false}
            onClose={closeSidebar}
            footer={footerButton}
            footerClassName="margin-right-10"
        >
            <SidebarHeader
                transportAssignment={transportAssignment}
                hasUnacknowledgedChanges={transportAssignment.hasUnacknowledgedExternalChanges}
            />
            <SidebarExpander transportAssignment={transportAssignment}>
                <TransportAssignmentConfirmationForm transportAssignment={transportAssignment} />
            </SidebarExpander>
            <TransportAssignmentSidebarBodyContent transportAssignment={transportAssignment} />
        </Sidebar>
    );
};
